<template>
  <!-- Add/Edit Role Modal -->
  <div class="modal fade dq-role-modal" id="role_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-role-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="submitForm">
          <!-- Header -->
          <div class="modal-header dq-modal-header">
            <div class="dq-titlewrap">
              <div
                class="dq-hero"
                :class="isEdit ? 'dq-hero-blue' : 'dq-hero-indigo'"
              >
                <i :class="isEdit ? 'ti ti-edit' : 'ti ti-shield-plus'"></i>
              </div>

              <div>
                <h4 class="modal-title dq-title mb-0">
                  {{ isEdit ? "Edit Role" : "Add Role" }}
                </h4>
                <div class="dq-subtitle">
                  {{
                    isEdit
                      ? "Update the role name and availability."
                      : "Create a role to group user permissions."
                  }}
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close custom-btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body dq-body pb-0">
            <div class="dq-card">
              <div class="dq-card-head">
                <span class="dq-badge dq-badge-indigo">
                  <i class="ti ti-user-shield"></i>
                </span>
                <div>
                  <div class="dq-h6">Role Details</div>
                  <div class="dq-help">
                    A clear name helps admins assign access faster.
                  </div>
                </div>
              </div>

              <div class="dq-card-body">
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label dq-label">
                      Role Name <span class="text-danger">*</span>
                    </label>
                    <input
                      v-model.trim="form.name"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. HR Manager"
                      required
                    />
                    <div class="dq-hint">
                      Use a descriptive name like <b>Payroll Admin</b>,
                      <b>HR</b>, <b>Employee</b>.
                    </div>
                  </div>

                  <div class="col-12">
                    <label class="form-label dq-label">Status</label>
                    <vue-select
                      v-model="form.status"
                      :options="statusOptions"
                      placeholder="Select status"
                    />
                    <div class="dq-hint">
                      Inactive roles can be hidden from assignment but remain in
                      history.
                    </div>
                  </div>

                  <!-- Preview pill -->
                  <div class="col-12">
                    <div class="dq-preview">
                      <div class="dq-preview-left">
                        <div class="dq-preview-title">
                          <i class="ti ti-eye me-1"></i> Preview
                        </div>
                        <div class="dq-preview-sub">
                          <span class="dq-pill dq-pill-role">
                            <i class="ti ti-shield"></i>
                            {{ form.name?.trim() ? form.name : "— Role name" }}
                          </span>

                          <span class="dq-pill ms-2" :class="statusPillClass">
                            <i class="ti ti-bolt"></i>
                            {{ form.status || "Active" }}
                          </span>
                        </div>
                      </div>
                      <div class="dq-preview-right">
                        <span class="dq-mini">
                          <i class="ti ti-info-circle"></i>
                          You’ll assign permissions in the role permissions
                          screen.
                        </span>
                      </div>
                    </div>
                  </div>
                  <!-- /Preview -->
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="isLoading"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{ isEdit ? "Save Changes" : "Add Role" }}
              <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-role-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-delete-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-delete-body">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1 dq-title">Confirm Delete</h4>
          <p class="mb-3 dq-subtitle">
            This action cannot be undone. Do you want to delete this role?
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-light dq-btn"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger dq-btn"
              @click="confirmDelete"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useRoleStore } from "@/stores/roleStore";
import { toast } from "@/utils/toast";

// Props & Emits
const props = defineProps({
  roleData: { type: Object, default: null },
  isEdit: { type: Boolean, default: false },
});
const emit = defineEmits(["saved"]);

const roleStore = useRoleStore();
const statusOptions = ["Active", "Inactive"];
const form = ref({ name: "", status: "Active" });
const deletingRoleId = ref(null);
const isLoading = computed(() => roleStore.loading);

// UX helpers
const statusPillClass = computed(() => {
  const s = String(form.value.status || "").toLowerCase();
  return s === "inactive" ? "dq-pill-muted" : "dq-pill-live";
});

// Sync props into form when editing
watch(
  () => props.roleData,
  (v) => {
    if (props.isEdit && v) {
      form.value = { name: v.name, status: v.status || "Active" };
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

function resetForm() {
  form.value = { name: "", status: "Active" };
}

async function submitForm() {
  try {
    if (props.isEdit && props.roleData?.id) {
      await roleStore.updateRole(props.roleData.id, form.value);
      toast("Role updated successfully");
    } else {
      await roleStore.createRole(form.value);
      toast("Role created successfully");
    }
    emit("saved");
    hideModal();
  } catch (err) {
    toast(err.response?.data?.message || "Error saving role", "error");
  }
}

function hideModal() {
  const modalEl = document.getElementById("role_modal");
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

// Keep your existing confirmDelete logic (not provided in snippet)
function confirmDelete() {
  // use your existing delete workflow
  // e.g. roleStore.deleteRole(deletingRoleId.value) ...
}

onMounted(() => {
  const el = document.getElementById("role_modal");
  el?.addEventListener("hidden.bs.modal", resetForm);
});
</script>

<style scoped>
/* Slightly wider than default md, but not huge */
.dq-role-dialog {
  max-width: 720px;
}
@media (max-width: 992px) {
  .dq-role-dialog {
    max-width: 96%;
  }
}
.dq-delete-dialog {
  max-width: 520px;
}

/* Content shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icon */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.14);
  color: rgb(37, 99, 235);
}

/* Body */
.dq-body {
  padding: 18px 22px 10px;
}

/* Card */
.dq-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
}
.dq-card-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 14px 14px 0 14px;
}
.dq-card-body {
  padding: 14px;
}
.dq-h6 {
  font-weight: 950;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Badge */
.dq-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}

/* Preview */
.dq-preview {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-preview-title {
  font-weight: 950;
}
.dq-preview-sub {
  margin-top: 6px;
  color: rgba(15, 23, 42, 0.68);
  font-size: 0.86rem;
}
.dq-mini {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.78);
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.1);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-muted {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}
.dq-pill-role {
  background: rgba(99, 102, 241, 0.1);
  color: rgb(79, 70, 229);
  border-color: rgba(99, 102, 241, 0.18);
}

/* Footer */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

/* delete body spacing */
.dq-delete-body {
  padding: 22px;
}
</style>
